import React from "react";
import type { ClassEnrollment, UserListItem } from "types/domain";
import { useLanguage } from "context/LanguageContext";
import type { OverlapInfo } from "../hooks/useSessionParticipantOverlap";
import ParticipantUserAvatar from "./ParticipantUserAvatar";
import SelectableParticipantCard from "./SelectableParticipantCard";

interface SessionStudentPickerProps {
  sessionStudentSearch: string;
  onSearchChange: (value: string) => void;
  students: UserListItem[];
  sessionStudentIds: string[];
  studentOverlapMap: Map<string, OverlapInfo>;
  enrollmentByStudentId: Map<string, ClassEnrollment>;
  setSessionStudentIds: React.Dispatch<React.SetStateAction<string[]>>;
  formatDob: (value?: string | null) => string;
}

const SessionStudentPicker = ({
  sessionStudentSearch,
  onSearchChange,
  students,
  sessionStudentIds,
  studentOverlapMap,
  enrollmentByStudentId,
  setSessionStudentIds,
  formatDob,
}: SessionStudentPickerProps): JSX.Element => {
  const { t } = useLanguage();
  const filteredStudents = React.useMemo(() => {
    const keyword = sessionStudentSearch.trim().toLowerCase();
    if (!keyword) return students;
    return students.filter((student) =>
      student.full_name.toLowerCase().includes(keyword)
    );
  }, [sessionStudentSearch, students]);

  return (
    <div className="mt-4 rounded-2xl border border-line bg-surface p-4">
      <div className="mb-3">
        <label className="mb-1.5 block text-xs font-medium text-gray-600 dark:text-gray-400">
          {t("sessionEditor.searchStudentsLabel")}
        </label>
        <input
          type="text"
          value={sessionStudentSearch}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={t("sessionEditor.searchStudentsPlaceholder")}
          className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none placeholder:text-gray-400 focus:border-brand-500"
        />
      </div>

      <div className="max-h-72 space-y-3 overflow-y-auto pr-1">
        {filteredStudents.length === 0 ? (
          <p className="rounded-xl border border-dashed border-line px-3 py-5 text-center text-xs text-ink-muted">
            {t("sessionEditor.noStudentsToPick")}
          </p>
        ) : (
          filteredStudents.map((student) => {
            const checked = sessionStudentIds.includes(student.id);
            const overlapInfo = studentOverlapMap.get(student.id);
            const hasScheduleConflict = Boolean(overlapInfo);
            const disabledByConflict = !checked && hasScheduleConflict;
            const enrollment = enrollmentByStudentId.get(student.id);

            return (
              <SelectableParticipantCard
                key={student.id}
                checked={checked}
                disabled={disabledByConflict}
                onCheckedChange={(isChecked) => {
                  setSessionStudentIds((prev) => {
                    if (isChecked) {
                      if (prev.includes(student.id)) return prev;
                      return [...prev, student.id];
                    }
                    return prev.filter((idValue) => idValue !== student.id);
                  });
                }}
                leading={<ParticipantUserAvatar user={student} />}
                content={
                  <>
                    <p className="truncate text-sm font-medium text-ink">
                      {student.full_name}
                    </p>
                    <p className="truncate text-xs text-ink-muted">
                      {student.email}
                    </p>
                    <p className="mt-1 text-xs text-ink-muted">
                      {t("sessionEditor.dobLine", {
                        dob: formatDob(student.dob),
                      })}
                    </p>
                    <p className="mt-1 text-xs text-ink-muted">
                      {t("sessionEditor.remainingSessions", {
                        count: enrollment?.remaining_sessions ?? "?",
                      })}
                    </p>
                  </>
                }
                trailing={
                  <>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        enrollment?.status === "ACTIVE"
                          ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                          : "bg-surface-muted text-gray-600 dark:text-gray-300"
                      }`}
                    >
                      {enrollment?.status === "ACTIVE"
                        ? t("enum.enrollmentStatus.ACTIVE")
                        : t("sessionEditor.enrollmentRegistered")}
                    </span>
                    <span className="text-xs text-ink-muted">
                      {checked
                        ? t("sessionEditor.participantJoined")
                        : disabledByConflict
                        ? t("sessionEditor.participantConflict")
                        : t("sessionEditor.participantNotSelected")}
                    </span>
                    {disabledByConflict && overlapInfo ? (
                      <span className="text-right text-[11px] text-red-500 dark:text-red-300">
                        {t("sessionEditor.overlapsWith", {
                          sessions: overlapInfo.sessionNames.join(", "),
                        })}
                      </span>
                    ) : null}
                  </>
                }
              />
            );
          })
        )}
      </div>
    </div>
  );
};

export default SessionStudentPicker;
